import React, { FC } from 'react';
import { SolutionModalProps } from './SolutionModalProps';
import CustomModal from '../CustomModal';

import { Divider, Stack } from '@mui/material';
import TextArea from '@ingka/text-area';
import Button from '@ingka/button';
import useTranslation from '../../hooks/useTranslation';
import {
  SOLUTION_CATEGORIES_LIST,
  SOLUTION_PURPOSES_LIST,
} from '../../utils/selectOptions';
import Select, { Option } from '@ingka/select';
import { ProjectSolutionType } from '../../models/project';
import { v4 } from 'uuid';
import ListView, { ListViewItem } from '@ingka/list-view';
import Text from '@ingka/text';
import SolutionCombobox from '../SolutionCombobox';

const SolutionModal: FC<SolutionModalProps> = ({
  isVisible,
  onClose,
  handleAddSolutions,
  solution,
  handleEditSolution,
  disabledFields,
  unsavedSolutions,
}) => {
  const t = useTranslation();
  const [solutionCategory, setSolutionCategory] = React.useState(
    SOLUTION_CATEGORIES_LIST[0] as string,
  );
  const [solutionType, setSolutionType] = React.useState('');
  const [solutionPurpose, setSolutionPurpose] = React.useState('');
  const [solutionDescription, setSolutionDescription] = React.useState('');
  const [solutions, setSolutions] = React.useState<ProjectSolutionType[]>([]);
  const emptySolutions = () => {
    setSolutionCategory('');
    setSolutionType('');
    setSolutionPurpose('');
    setSolutionDescription('');
  };
  React.useEffect(() => {
    if (!!solution) {
      setSolutionCategory(solution.solutionCategory);
      setSolutionType(solution.solutionType);
      setSolutionPurpose(solution.solutionPurpose);
      setSolutionDescription(solution.solutionDescription);
    } else {
      emptySolutions();
    }
  }, [solution]);
  React.useEffect(() => {
    setSolutions(unsavedSolutions);
  }, [unsavedSolutions]);
  const submitHandler = () => {
    if (!!solution) {
      handleEditSolution({
        ...solution,
        solutionCategory,
        solutionType,
        solutionPurpose,
        solutionDescription,
      });
    } else {
      handleAddSolutions(solutions);
    }
    emptySolutions();
    setSolutions([]);
  };
  const addSolutionHanlder = () => {
    const solutionsCopy = [
      ...solutions,
      {
        id: `unsaved-${v4()}`,
        solutionCategory,
        solutionType,
        solutionPurpose,
        solutionDescription,
      },
    ];
    setSolutions(solutionsCopy);
    emptySolutions();
  };
  const solutionTypeChangedHanlder = (value) => {
    const [category, type] = value.split('-');
    setSolutionType(type);
    setSolutionCategory(category);
  };
  const returnComboboxValue = () => {
    if (!solutionCategory || !solutionType) return [];
    return [`${solutionCategory}-${solutionType}`];
  };
  const removeSolutionHandler = (id) => {
    const filteredSolutions = solutions.filter(
      (solution) => solution.id !== id,
    );
    setSolutions(filteredSolutions);
  };
  return (
    <CustomModal
      isVisible={isVisible}
      onClose={onClose}
      title={'Solution'}
      onBackBtnClick={onClose}
      children={
        <Stack height={'100%'}>
          <Stack spacing={2} p={2} px={4} height={'100%'} flex={1}>
            <Text textStyle={'Body.M'} colour={false} tagName={'p'}>
              Add solutions to your project. Solution categories are
              automatically added when you choose a solution type.
            </Text>
            <Stack>
              <SolutionCombobox
                value={returnComboboxValue()}
                onChange={(category, type) =>
                  solutionTypeChangedHanlder(`${category}-${type}`)
                }
                disabled={disabledFields?.includes('ALL')}
              />
            </Stack>
            <Select
              id={`solutionPurpose-select`}
              label={'Solution Purpose (optional)'}
              onChange={(e) => {
                setSolutionPurpose(e.target.value);
              }}
              disabled={disabledFields?.includes('ALL')}
              value={solutionPurpose}
            >
              {SOLUTION_PURPOSES_LIST.map((el, index) => (
                <Option
                  key={el + Math.random() * index}
                  value={el}
                  name={t(`solutionPuposes.${el}`)}
                />
              ))}
            </Select>

            <TextArea
              id={`solution-description-id`}
              label={`${t('labels.solutionDescription')} (optional)`}
              onChange={(e) => setSolutionDescription(e.target.value)}
              value={solutionDescription}
              disabled={disabledFields?.includes('ALL')}
            />
            <div>
              {!solution && (
                <Button
                  type={'primary'}
                  size={'xsmall'}
                  fluid={false}
                  onClick={addSolutionHanlder}
                  disabled={!solutionCategory || !solutionType}
                  text={'Add solution'}
                />
              )}
            </div>
            <Divider />
            {!solution && (
              <ListView id={`solution-list-view`}>
                {solutions.map((solution) => (
                  <ListViewItem
                    title={t(`solutionCategories.${solution.solutionCategory}`)}
                    description={t(`solutionTypes.${solution.solutionType}`)}
                    quantityLabel={
                      <Button
                        text={'Delete'}
                        type={'secondary'}
                        size={'xsmall'}
                        onClick={() => removeSolutionHandler(solution.id)}
                      />
                    }
                  />
                ))}
              </ListView>
            )}
          </Stack>
          <Stack direction={'row'} spacing={2} p={2}>
            <Button
              type={'secondary'}
              style={{ width: '100%' }}
              onClick={onClose}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type={'primary'}
              style={{ width: '100%' }}
              onClick={submitHandler}
              disabled={
                !!solution
                  ? disabledFields?.includes('ALL')
                  : disabledFields?.includes('ALL') || !solutions.length
              }
            >
              {!!solution ? 'Update' : 'Save'}
            </Button>
          </Stack>
        </Stack>
      }
    />
  );
};

export default SolutionModal;
